import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, X, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface InstructionModalProps {
  instruction: {
    id: string;
    title: string;
    content: string;
    created_at: string;
    updated_at: string;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

const InstructionModal: React.FC<InstructionModalProps> = ({
  instruction,
  isOpen,
  onClose
}) => {
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Można dodać toast notification
      console.log('Instrukcja skopiowana do schowka');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!instruction) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="h-5 w-5 text-blue-500" />
            {instruction.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Metadane instrukcji */}
          <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border pb-2 mb-3">
            <div className="flex items-center gap-3">
              <span className="opacity-60">Utworzona: {formatDate(instruction.created_at)}</span>
              <span className="opacity-60">Zaktualizowana: {formatDate(instruction.updated_at)}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(instruction.content)}
              className="flex items-center gap-1 text-xs hover:bg-muted px-2 py-1"
            >
              <Copy className="h-3 w-3" />
              Kopiuj
            </Button>
          </div>

          {/* Treść instrukcji w markdown */}
          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-li:text-foreground">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => <h1 className="text-lg font-bold mb-3 text-foreground">{children}</h1>,
                h2: ({children}) => <h2 className="text-base font-semibold mb-2 mt-4 text-foreground">{children}</h2>,
                h3: ({children}) => <h3 className="text-sm font-medium mb-2 mt-3 text-foreground">{children}</h3>,
                p: ({children}) => <p className="mb-3 text-sm text-foreground">{children}</p>,
                ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1 ml-4">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1 ml-4">{children}</ol>,
                li: ({children}) => <li className="text-sm text-foreground">{children}</li>,
                strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                em: ({children}) => <em className="italic text-foreground">{children}</em>,
                code: ({children}) => <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-foreground">{children}</code>,
                blockquote: ({children}) => <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground">{children}</blockquote>,
              }}
            >
              {instruction.content}
            </ReactMarkdown>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstructionModal;
